import type { TtqibCanvasObject, TtqibViewport } from './types';
import { TTQIB_CARD_WIDTH } from './ttqibObjectLayout';

/**
 * 品题页顶栏下方浮层控件的 top 偏移。
 * 顶栏约 72px + pt-2(8px) ≈ 80px，再留间隙，避免与「前往多对一 / 缩放」等控件重叠。
 */
export const TTQ_SESSION_OVERLAY_TOP_CLASS = 'top-[5.75rem]';

type RawViewport = Partial<TtqibViewport> & { scale?: number };

/** 画布缩放上下限（两指捏合 / ⌃滚轮共用） */
export const TTQIB_ZOOM_MIN = 0.22;
export const TTQIB_ZOOM_MAX = 2.5;

export function normalizeViewport(raw?: RawViewport | null): TtqibViewport {
  const zoomRaw = raw?.zoom ?? raw?.scale ?? 1;
  const zoom = Number.isFinite(zoomRaw)
    ? Math.min(TTQIB_ZOOM_MAX, Math.max(TTQIB_ZOOM_MIN, zoomRaw))
    : 1;
  const x = Number.isFinite(raw?.x) ? raw!.x! : 0;
  const y = Number.isFinite(raw?.y) ? raw!.y! : 0;
  return { x, y, zoom };
}

/**
 * 多对一关系层：收敛卡相对 viewport.zoom 的反向放大，
 * 使屏幕上接近一对多整卡可读尺寸（约抵消缩小）。
 */
export function ttqConvergeLodScreenBoost(zoom: number): number {
  const z = normalizeViewport({ zoom }).zoom;
  return Math.min(2.6, Math.max(1, 0.92 / z));
}

export function isViewportUsable(raw?: RawViewport | null): boolean {
  if (!raw) return false;
  const zoom = raw.zoom ?? raw.scale;
  return Number.isFinite(raw.x) && Number.isFinite(raw.y) && Number.isFinite(zoom) && (zoom ?? 0) > 0;
}

export function computeObjectsBounds(objects: readonly TtqibCanvasObject[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} | null {
  if (!objects.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const object of objects) {
    const width = object.width ?? TTQIB_CARD_WIDTH;
    const height = object.height ?? 120;
    minX = Math.min(minX, object.x);
    minY = Math.min(minY, object.y);
    maxX = Math.max(maxX, object.x + width);
    maxY = Math.max(maxY, object.y + height);
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function computeFitViewport(
  objects: readonly TtqibCanvasObject[],
  containerWidth: number,
  containerHeight: number,
  padding = 48,
): TtqibViewport {
  const bounds = computeObjectsBounds(objects);
  if (!bounds || containerWidth <= 0 || containerHeight <= 0) {
    return { x: 40, y: 40, zoom: 1 };
  }

  const contentWidth = Math.max(bounds.maxX - bounds.minX, 320);
  const contentHeight = Math.max(bounds.maxY - bounds.minY, 240);
  const usableWidth = Math.max(containerWidth - padding * 2, 200);
  const usableHeight = Math.max(containerHeight - padding * 2, 200);
  const zoom = Math.min(1.2, Math.max(TTQIB_ZOOM_MIN, Math.min(usableWidth / contentWidth, usableHeight / contentHeight)));

  const x = padding - bounds.minX * zoom;
  const y = padding - bounds.minY * zoom;

  return { x, y, zoom };
}

/**
 * 首次进入默认视口：约可见 2.5 列卡片宽度，锚定内容左上角。
 * 不要用 fit 全图——颗粒多时会缩得过小。
 */
/** 视口 zoom ≤ 此值时，从一对多进入多对一（鸟瞰）LOD */
export const TTQIB_BIRDS_EYE_ZOOM_THRESHOLD = 0.52;

/** 视口 zoom ≥ 此值时，从多对一退出到一对多（迟滞带上沿，避免阈值附近闪烁） */
export const TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD = 0.58;

/** 瞬时判定（无迟滞）；页面级模式请用 useTtqCanvasLodMode */
export function isTtqBirdsEyeZoomLevel(zoom: number): boolean {
  return normalizeViewport({ zoom }).zoom <= TTQIB_BIRDS_EYE_ZOOM_THRESHOLD;
}

/** 带迟滞的模式解析：prev 为当前模式，zoom 为新视口缩放 */
export function resolveTtqBirdsEyeMode(previousBirdsEye: boolean, zoom: number): boolean {
  const z = normalizeViewport({ zoom }).zoom;
  if (previousBirdsEye) {
    return z < TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD;
  }
  return z <= TTQIB_BIRDS_EYE_ZOOM_THRESHOLD;
}

/** 快捷按钮「前往一对多」目标 zoom，明确高于退出阈值 */
export function ttqZoomForDeriveMode(): number {
  return Math.min(1.15, TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD + 0.08);
}

export function computeDefaultColumnViewport(
  objects: readonly TtqibCanvasObject[],
  containerWidth: number,
  containerHeight: number,
  columnsVisible = 2.5,
): TtqibViewport {
  const bounds = computeObjectsBounds(objects);
  const padding = 48;
  const topChrome = 72;
  const usableWidth = Math.max(containerWidth - padding * 2, 280);
  const targetWorldWidth = Math.max(columnsVisible * TTQIB_CARD_WIDTH, TTQIB_CARD_WIDTH);
  const zoom = Math.min(1.15, Math.max(TTQIB_ZOOM_MIN, usableWidth / targetWorldWidth));
  const minX = bounds?.minX ?? 80;
  const minY = bounds?.minY ?? 40;
  void containerHeight;
  return {
    x: padding - minX * zoom,
    y: padding + topChrome - minY * zoom,
    zoom,
  };
}
